import { createResourceHooks, type ResourceMutationDefinition } from '@/lib/query/resourceFactory';
import { fetchSiteSettings, updateSiteSettingsForUser } from '@/lib/services/siteSettings';
import type { AppUser } from '@/lib/auth/user';
import type { SiteSettings, SiteSettingsUpdateInput } from '@/lib/types/siteSettings';
import type { QueryKey } from '@tanstack/react-query';

export interface SiteSettingsDetailParams {
  slug: string;
}

export interface UpdateSiteSettingsVariables {
  user: AppUser | null;
  values: SiteSettingsUpdateInput;
}

const scope = 'site-settings';
const primaryDetailParams: SiteSettingsDetailParams = { slug: 'primary' } as const;
const listParams: Record<string, never> = {};
const detailQueryKey: QueryKey = [scope, 'detail', primaryDetailParams.slug];
const listQueryKey: QueryKey = [scope, 'list', 'singleton'];

type SiteSettingsMutations = {
  update: ResourceMutationDefinition<UpdateSiteSettingsVariables, SiteSettings>;
};

export const siteSettingsResource = createResourceHooks<
  Record<string, never>,
  SiteSettings,
  SiteSettingsDetailParams,
  SiteSettings,
  SiteSettingsMutations
>({
  scope,
  defaults: {
    staleTime: 5 * 60 * 1000,
  },
  list: {
    key: () => 'singleton',
    fetch: ({ signal }) => fetchSiteSettings({ signal }),
  },
  detail: {
    key: (params) => params.slug,
    fetch: ({ signal }) => fetchSiteSettings({ signal }),
  },
  mutations: {
    update: {
      mutationFn: ({ user, values }) => updateSiteSettingsForUser({ user, values }),
      invalidate: () => [detailQueryKey, listQueryKey],
    },
  },
});

export function useSiteSettingsDetail() {
  return siteSettingsResource.useDetail(primaryDetailParams);
}

export function useSiteSettingsList() {
  return siteSettingsResource.useList(listParams);
}
